import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { updateOrg } from '@/services/org/update-org';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';
import { ConfirmationModal } from '@/components/confirmation-modal';

export const OrgAssetMode = () => {
  const { currentOrg, updateOrg: updateOrgInStore } = useOrgStore();
  const notification = useNotification();
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);
  const [isLoadingOverride, setIsLoadingOverride] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'toggle_default' | 'toggle_override' | null
  >(null);
  const [pendingValue, setPendingValue] = useState<boolean>(false);

  const isPublic = currentOrg?.default_asset_mode === 'public';
  const allowOverride = currentOrg?.allow_public_media_override ?? false;

  const handleToggleDefault = (checked: boolean) => {
    setPendingValue(checked);
    setConfirmAction('toggle_default');
  };

  const handleToggleOverride = (checked: boolean) => {
    setPendingValue(checked);
    setConfirmAction('toggle_override');
  };

  const executeToggleDefault = async () => {
    if (!currentOrg?.orgid) return;
    setConfirmAction(null);
    setIsLoadingDefault(true);
    try {
      const newMode = pendingValue ? 'public' : 'private';
      const result = await updateOrg({ default_asset_mode: newMode });
      if (result?.detail) {
        notification.error(result.detail);
        return;
      }
      updateOrgInStore(currentOrg.orgid, { default_asset_mode: newMode });
      notification.success(`Default file URL mode set to ${newMode}`);
    } catch (error) {
      console.error('Update asset mode error:', error);
      Sentry.captureException(error, {
        extra: { context: 'update-org-asset-mode' },
      });
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoadingDefault(false);
    }
  };

  const executeToggleOverride = async () => {
    if (!currentOrg?.orgid) return;
    setConfirmAction(null);
    setIsLoadingOverride(true);
    try {
      const result = await updateOrg({
        allow_public_media_override: pendingValue,
      });
      if (result?.detail) {
        notification.error(result.detail);
        return;
      }
      updateOrgInStore(currentOrg.orgid, {
        allow_public_media_override: pendingValue,
      });
      notification.success(
        pendingValue
          ? 'Workflow creators can now override to public'
          : 'Public override disabled',
      );
    } catch (error) {
      console.error('Update override setting error:', error);
      Sentry.captureException(error, {
        extra: { context: 'update-org-asset-override' },
      });
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoadingOverride(false);
    }
  };

  const getConfirmationProps = () => {
    if (confirmAction === 'toggle_default') {
      return {
        title: pendingValue
          ? 'Enable public file URLs?'
          : 'Switch to private file URLs?',
        description: pendingValue
          ? 'All workflows in this organization will produce permanent, publicly accessible URLs for generated files by default. This cannot be undone without manually updating affected workflows.'
          : 'All workflows will produce private, expiring URLs by default. Workflows currently set to public will continue using public URLs unless individually changed.',
        buttonLabel: pendingValue ? 'Enable public' : 'Switch to private',
      };
    }
    return {
      title: pendingValue
        ? 'Allow workflow-level override?'
        : 'Disable workflow-level override?',
      description: pendingValue
        ? 'Workflow creators will be able to switch individual workflows to produce public, permanent file URLs even though the organization default is private.'
        : 'Workflow creators will no longer be able to override the private default. Existing workflows set to public will remain public until manually changed.',
      buttonLabel: pendingValue ? 'Allow override' : 'Disable override',
    };
  };

  return (
    <>
      <Card>
        <CardContent className='flex flex-col gap-5'>
          <div>
            <div className='text-zinc-900 dark:text-zinc-50'>
              Generated file URLs
            </div>
            <p className='text-sm text-muted-foreground mt-1'>
              Control how AI-generated files (images, documents, media) are
              stored and shared. Public mode creates permanent URLs suitable for
              publishing to external platforms like WordPress or CMS systems.
            </p>
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex flex-col gap-0.5'>
              <Label htmlFor='asset-mode-toggle'>
                Default to permanent public URLs
              </Label>
              <p className='text-xs text-muted-foreground'>
                When enabled, all workflows produce non-expiring, publicly
                accessible URLs for generated files
              </p>
            </div>
            {isLoadingDefault ? (
              <LoadingSpinner />
            ) : (
              <Switch
                id='asset-mode-toggle'
                checked={isPublic}
                onCheckedChange={handleToggleDefault}
                disabled={isLoadingDefault}
              />
            )}
          </div>

          {!isPublic && (
            <div className='flex items-center justify-between'>
              <div className='flex flex-col gap-0.5'>
                <Label htmlFor='asset-override-toggle'>
                  Allow workflow-level override
                </Label>
                <p className='text-xs text-muted-foreground'>
                  Let workflow creators switch individual workflows to public
                  URLs
                </p>
              </div>
              {isLoadingOverride ? (
                <LoadingSpinner />
              ) : (
                <Switch
                  id='asset-override-toggle'
                  checked={allowOverride}
                  onCheckedChange={handleToggleOverride}
                  disabled={isLoadingOverride}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={confirmAction !== null}
        handleCancel={() => setConfirmAction(null)}
        handleDelete={
          confirmAction === 'toggle_default'
            ? executeToggleDefault
            : executeToggleOverride
        }
        isLoading={
          confirmAction === 'toggle_default'
            ? isLoadingDefault
            : isLoadingOverride
        }
        {...getConfirmationProps()}
      />
    </>
  );
};
